import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { Clock, ArrowRight, User, Loader2 } from 'lucide-react';

/**
 * Compact, scrollable audit timeline for a single document's VAT claim
 * history. Always read-only.
 */
const fmtPeriod = (p) => {
  if (!p) return '—';
  if (p.includes('Q')) {
    const [y, q] = p.split('-Q');
    return `Q${q} ${y}`;
  }
  return p;
};

const ACTION_META = {
  defer:    { color: 'amber',  label: 'تَأجيل' },
  forfeit:  { color: 'red',    label: 'سَقَط بِالتَّقادُم' },
  claim:    { color: 'green',  label: 'تَمَّت المُطالَبة' },
  claim_via_filing: { color: 'green', label: 'مُطالَبة بِتَقديم إقرار' },
  reclaim:  { color: 'indigo', label: 'إِعادَة لِلجاهز' },
  auto_expire: { color: 'red', label: 'سُقوط تِلقائي' },
};

export const VATClaimAuditTimeline = ({ documentId, documentType }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!documentId) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(`/vat-claims/${documentId}/audit-log`, {
          params: { document_type: documentType },
        });
        setEntries(data.entries || []);
      } catch {
        setEntries([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [documentId, documentType]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Loader2 className="w-3 h-3 animate-spin" />
        جارٍ تَحميل سِجِل التَّدقيق...
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic">
        لا تُوجَد حَرَكات سابِقَة على هَذه الفاتورة بَعد.
      </div>
    );
  }
  return (
    <ol
      className="relative border-s-2 border-slate-200 ms-2 space-y-3"
      data-testid="vat-claim-audit-timeline"
    >
      {entries.map((e) => {
        const meta = ACTION_META[e.action] || { color: 'slate', label: e.action };
        const tone = {
          amber: 'bg-amber-500',
          red:   'bg-red-500',
          green: 'bg-green-500',
          indigo:'bg-indigo-500',
          slate: 'bg-slate-500',
        }[meta.color];
        return (
          <li key={e.id} className="ms-3">
            <span
              className={`absolute -start-1.5 mt-1 w-3 h-3 rounded-full ${tone} ring-2 ring-white`}
            />
            <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-200">
              <div className="flex items-center justify-between text-xs">
                <span className={`font-semibold text-${meta.color}-700`}>{meta.label}</span>
                <span className="text-slate-500">
                  <Clock className="w-3 h-3 inline me-1" />
                  {(e.created_at || '').slice(0, 16).replace('T', ' ')}
                </span>
              </div>
              <div className="text-xs text-slate-700 mt-1 flex items-center gap-1">
                <span className="font-mono">{fmtPeriod(e.from_period)}</span>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <span className="font-mono font-semibold">{fmtPeriod(e.to_period)}</span>
                {e.vat_amount ? (
                  <span className="ms-2 text-slate-500">
                    · {Number(e.vat_amount).toFixed(2)} SAR
                  </span>
                ) : null}
              </div>
              {e.reason && (
                <div className="text-[11px] text-slate-600 mt-1 italic">"{e.reason}"</div>
              )}
              <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2">
                <User className="w-3 h-3" />
                <span>{e.actor_name || e.actor_id || '—'}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 uppercase font-bold">
                  {e.source}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default VATClaimAuditTimeline;
